/**
 * `<DraftBar>` — the row above the grid while an authored screen has unpublished
 * changes. The owner's text mock, approved 2026-10-06:
 *
 *     ✎ Draft — not published · edited 2 min ago by you      [Discard] [Publish v4]
 *
 * Drawn only in Author as, on that group's own screen, once a draft exists — the
 * host decides that and renders nothing otherwise, so a member never sees it.
 * No draft, no bar: the first edit creates the draft and the bar appears.
 *
 * **"you", or a name.** Group Admins share one draft per screen (decision 1),
 * so the line says who last touched it — "you" when that is the person signed
 * in, never the group account.
 *
 * Nothing here is async and nothing here writes; the host owns Discard and
 * Publish, which are server calls, exactly as `ScreenTabs` leaves its writes to
 * its host.
 */

import type { DraftMeta } from "../lib/screen-draft";

export interface DraftBarProps {
  meta: DraftMeta;
  /** The person signed in (`/auth/me`'s `actorKey`), to say "you". */
  actorKey: string | null | undefined;
  /** The screen's current published version — the button offers the next one. */
  version: number | undefined;
  busy?: boolean;
  error?: string | null;
  onDiscard: () => void;
  onPublish: () => void;
  now?: number;
}

export function DraftBar({
  meta,
  actorKey,
  version,
  busy = false,
  error,
  onDiscard,
  onPublish,
  now,
}: DraftBarProps) {
  const who =
    actorKey && meta.updatedBy === actorKey ? "you" : (meta.updatedByName ?? meta.updatedBy) || "someone";
  // A screen published before versions were recorded has none; its next is v1.
  const next = (version ?? 0) + 1;

  return (
    <div role="status" aria-label="Unpublished draft" style={BAR}>
      <span style={MESSAGE}>
        <span aria-hidden="true">✎</span> Draft — not published · edited {editedAgo(meta.updatedAt, now)} by {who}
      </span>
      {error && <span style={ERROR}>{error}</span>}
      <button type="button" style={DISCARD} disabled={busy} onClick={onDiscard}>
        Discard
      </button>
      <button type="button" style={PUBLISH} disabled={busy} onClick={onPublish}>
        Publish v{next}
      </button>
    </div>
  );
}

/**
 * `<DraftUnreadBar>` — the draft could not be read, so nothing typed here is saved.
 *
 * `ScreenerView` stops writing when the draft load fails, because filling from the
 * published screen and saving would overwrite the real draft. That stop was
 * silent: on 2026-10-16, after a Fly outage, an author's formats sat on screen,
 * were never saved, and were gone on reload. This says so before the work is done.
 */
export function DraftUnreadBar({ busy = false, onRetry }: { busy?: boolean; onRetry: () => void }) {
  return (
    <div role="alert" aria-label="Draft not loaded" style={BAR}>
      <span style={{ ...MESSAGE, color: "var(--qf-negative)" }}>
        <span aria-hidden="true">⚠</span> Couldn&rsquo;t load the draft — changes here are not being saved.
      </span>
      <button type="button" style={PUBLISH} disabled={busy} onClick={onRetry}>
        {busy ? "Trying…" : "Try again"}
      </button>
    </div>
  );
}

/** "2 min ago", in the words the mock uses. */
export function editedAgo(iso: string, now = Date.now()): string {
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return "just now";
  const seconds = Math.max(0, (now - then) / 1000);
  if (seconds < 60) return "just now";
  if (seconds < 3600) return `${Math.round(seconds / 60)} min ago`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)} h ago`;
  const days = Math.round(seconds / 86400);
  return `${days} ${days === 1 ? "day" : "days"} ago`;
}

const BAR: React.CSSProperties = {
  flex: "0 0 auto",
  display: "flex",
  alignItems: "center",
  // `UpdateBar`'s fallback on the narrowest phone: the buttons wrap to a second
  // line rather than the row forcing a horizontal scroll.
  flexWrap: "wrap",
  gap: "0.25rem 0.6rem",
  padding: "0.25rem 0.6rem",
  background: "var(--qf-surface-alt)",
  color: "var(--qf-text)",
  borderBottom: "1px solid var(--qf-border)",
  fontSize: "0.78rem",
  lineHeight: 1.3,
};

const MESSAGE: React.CSSProperties = { minWidth: 0, marginRight: "auto" };

const ERROR: React.CSSProperties = { color: "var(--qf-negative)" };

const DISCARD: React.CSSProperties = {
  padding: "0.15rem 0.55rem",
  background: "transparent",
  color: "var(--qf-text-secondary)",
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  font: "inherit",
  cursor: "pointer",
};

const PUBLISH: React.CSSProperties = {
  padding: "0.15rem 0.65rem",
  background: "var(--qf-accent)",
  color: "var(--qf-background)",
  border: "none",
  borderRadius: 4,
  font: "inherit",
  fontWeight: 600,
  cursor: "pointer",
};
